import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { AngularNavLink } from '../shared/AngularNavLink';
import { ProductApprovalItem } from '@texas/api/endpoints/search/searchPaTypes';
import { Box, Flex, Text } from '@chakra-ui/react';
import { MirrorTag } from '../shared/suppliers/MirrorTag';
import { MirroringTag } from '../shared/suppliers/MirroringTag';
import { RefObject } from 'react';
import { PaMenuPopover } from '../article/productApproval/PaMenuPopover';
import { productApprovalEvents } from '../shared/productApproval/events';
import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { SampleState } from '@texas/components/shared/samples/SampleState';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { RejectReasons } from '../article/productApproval/shared/RejectReasons';

export const PaTableColumns = (
  t: TFunction,
  containerRef: RefObject<HTMLDivElement>,
) => {
  const columnHelper = createColumnHelper<ProductApprovalItem>();
  const columns = [
    columnHelper.accessor('formatNumber', {
      cell: (info) => (
        <AngularNavLink
          to="article.productApproval"
          navobj={{
            articleId: info.row.original.articleId,
            searchTerm: info.getValue(),
            state: info.row.original.state,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('productApproval.number'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('itemCode', {
      cell: (info) => (
        <AngularNavLink
          to="article"
          navobj={{
            articleId: info.row.original.articleId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.itemCode'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('variantName', {
      cell: (info) => (
        <AngularNavLink
          to="variant"
          navobj={{
            articleId: info.row.original.articleId,
            variantId: info.row.original.variantId,
          }}
        >
          <Flex direction="column">
            <Box>{info.getValue()}</Box>
            {info.row.original.matrixNodeXValue &&
              info.row.original.matrixNodeYValue && (
                <>
                  <Text variant="sub">
                    {info.row.original.matrixNodeXValue}
                  </Text>
                  <Text variant="sub">
                    {info.row.original.matrixNodeYValue}
                  </Text>
                </>
              )}
          </Flex>
        </AngularNavLink>
      ),
      header: t('variant.variant'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('customerName', {
      cell: (info) => (
        <AngularNavLink
          to="customer"
          navobj={{
            customerId: info.row.original.customerId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.brand'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('concept', {
      cell: (info) => (
        <AngularNavLink
          to="concept"
          navobj={{
            conceptId: info.row.original.conceptId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.concept'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('general.description'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('branchIdentifier', {
      cell: (info) => info.getValue(),
      header: t('general.branch'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('supplierName', {
      cell: (info) => (
        <Flex direction="column" gap={1}>
          <Text whiteSpace="normal">{info.getValue()}</Text>
          {info.row.original.mainSupplier && (
            <MirrorTag
              mainSupplier={info.row.original.mainSupplier}
              containerRef={containerRef}
            />
          )}
          {info.row.original.followers &&
            info.row.original.followers.length > 0 && (
              <MirroringTag
                showCount={true}
                followers={info.row.original.followers}
                containerRef={containerRef}
              />
            )}
        </Flex>
      ),
      header: t('suppliers.supplier'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('created', {
      cell: (info) => formatDate(Locale.En, info.getValue()),
      header: t('article.created'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('sample', {
      cell: (info) => {
        const sample = info.getValue();
        const lifecycle = sample ? sample.lifecycle : SampleLifecycle.Nothing;
        return (
          <SampleState
            lifecycle={ensureEnumNumber(SampleLifecycle, lifecycle)}
            sampleReadyDate={info.row.original.sample?.sampleReadyDate ?? null}
            handledNote={info.row.original.handledNote}
          />
        );
      },
      header: t('sample.sample'),
      enableSorting: false,
      maxSize: 100,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('handledNote', {
      cell: (info) => (
        <Flex direction="column" gap={1}>
          <RejectReasons reasons={info.row.original.handledReason} />
          <Text whiteSpace="normal">{info.getValue() ?? ''}</Text>
        </Flex>
      ),
      header: t('productApproval.handledNote'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: false },
    }),
    columnHelper.accessor('state', {
      cell: (info) => (
        <Flex direction="column">
          <Text w="16">{info.getValue()}</Text>
          {info.row.original.handledDate && (
            <Text>{formatDate(Locale.En, info.row.original.handledDate)}</Text>
          )}
        </Flex>
      ),
      header: t('general.state'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('id', {
      cell: (info) => (
        <Flex gap={1}>
          {!info.row.original.mainSupplier && (
            <PaMenuPopover
              productApproval={info.row.original}
              onChanged={() =>
                productApprovalEvents.productApprovalChanged.dispatch({
                  id: info.row.original.id,
                })
              }
            />
          )}
        </Flex>
      ),
      header: '',
    }),
  ];

  return columns;
};
